// Mentions/lenker i chat: brukeren skriver «@» og velger en plass/rute/pakkeliste
// som allerede ligger i mappa. Valget lagres i meldingsteksten som et token
// `@[spot:ID|Etikett]`, og rendres som en klikkbar chip.

export type MentionKind = 'spot' | 'route' | 'list';

export interface MentionToken {
  type: 'mention';
  kind: MentionKind;
  id: string;
  label: string;
}
export interface TextSegment {
  type: 'text';
  value: string;
}
export type ChatSegment = TextSegment | MentionToken;

// Etiketter kan ikke inneholde `|` eller `]` — de ville brutt token-syntaksen.
function sanitizeLabel(label: string): string {
  return (label || '').replace(/[|\]]/g, ' ').trim() || '?';
}

export function buildMentionToken(kind: MentionKind, id: string, label: string): string {
  return `@[${kind}:${id}|${sanitizeLabel(label)}]`;
}

// Fanger `@[kind:id|label]`. id: alt utenom `|` og `]`. label: alt utenom `]`.
const MENTION_RE = /@\[(spot|route|list):([^|\]]+)\|([^\]]+)\]/g;

/**
 * Deler en meldingstekst i vanlige tekstbiter og mention-tokens, i rekkefølge,
 * slik at renderen kan vise chips inline.
 */
export function parseMentions(text: string): ChatSegment[] {
  const segments: ChatSegment[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(text)) !== null) {
    if (m.index > lastIndex) {
      segments.push({ type: 'text', value: text.slice(lastIndex, m.index) });
    }
    segments.push({ type: 'mention', kind: m[1] as MentionKind, id: m[2], label: m[3] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex) });
  }
  return segments;
}

/** Ren tekst uten token-syntaks (for previews/varsler): «@[spot:x|Ved elven]» → «Ved elven». */
export function stripMentions(text: string): string {
  return (text || '').replace(MENTION_RE, (_all, _kind, _id, label) => label);
}

/**
 * Finner en aktiv «@…»-skriving rett før markøren, for autocomplete. Returnerer
 * søkeordet og hvor det starter, eller null hvis markøren ikke står i en mention.
 */
export function activeMentionQuery(text: string, caret: number): { query: string; start: number } | null {
  const upto = text.slice(0, caret);
  // Siste «@» som ikke allerede er del av et ferdig token og ikke har mellomrom etter seg
  const at = upto.lastIndexOf('@');
  if (at === -1) return null;
  // «@» må stå i starten eller etter mellomrom/linjeskift for å telle som mention-trigger
  if (at > 0 && !/\s/.test(upto[at - 1])) return null;
  const query = upto.slice(at + 1);
  // Avbryt hvis søkeordet inneholder mellomrom/linjeskift eller token-tegn (da er det ikke lenger en mention)
  if (/[\s[\]|]/.test(query)) return null;
  return { query, start: at };
}
